import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Form, useModel, useSettings, useUserData, Utils } from 'app/main/components';
import { DateField, ModuleSearchField, NumberTextField } from 'app/main/components/fields';

interface SkillsEvaluationsEditDialogProps {
	model: any;
	isAdd?: boolean;
	updateRefresh: VoidFunction;
}

export const showSkillsEvaluationsEditDialog = (props: SkillsEvaluationsEditDialogProps) =>
	openDialog({
		children: <SkillsEvaluationsEditDialog {...props} />,
		maxWidth: 'xs',
		fullWidth: true,
		onClose: () => {}
	});

export function SkillsEvaluationsEditDialog(props: SkillsEvaluationsEditDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('skills_evaluations');
	const userData = useUserData();

	const modelProps = useModel({
		settings,
		baseModel: props.isAdd
			? { skev_uid: uuidv4(), skev_user_created_uid: userData.user_uid, ...props.model }
			: props.model,
		isEdit: true,
		isAdd: props.isAdd
	});

	function submitDialog() {
		modelProps.save({
			onSuccess: () => {
				dispatch(Utils.alertSuccessMsg(t));
				dispatch(closeDialog());
				props.updateRefresh?.();
			}
		});
	}

	return (
		<>
			<DialogTitle>
				{t(props.isAdd ? 'DIALOG_SKILLS_EVALUATIONS_ADD_TITLE' : 'DIALOG_SKILLS_EVALUATIONS_EDIT_TITLE')}
			</DialogTitle>
			<DialogContent>
				<Form id="skillsEvaluationsEditDialog" onSubmit={submitDialog}>
					<Grid container spacing={2}>
						<Grid size={12}>
							<ModuleSearchField
								name="skev_skll_uid"
								label={t('DETAILS_SKILLS_EVALUATIONS_FIELD_SKILL')}
								module="skills"
								uid={modelProps.model.skev_skll_uid}
								isEdit
								sendChange={modelProps.sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={12}>
							<ModuleSearchField
								name="skev_user_assigned_uid"
								label={t('DETAILS_SKILLS_EVALUATIONS_FIELD_USER_ASSIGNED')}
								module="users"
								uid={modelProps.model.skev_user_assigned_uid}
								isEdit
								sendChange={modelProps.sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={12}>
							<NumberTextField
								name="skev_level"
								label={t('DETAILS_SKILLS_EVALUATIONS_FIELD_LEVEL')}
								value={modelProps.model.skev_level}
								isEdit
								step={10}
								measureUnit="%"
								sendChange={modelProps.sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							<DateField
								name="skev_d_from"
								label={t('DETAILS_SKILLS_EVALUATIONS_FIELD_DATE_FROM')}
								value={modelProps.model.skev_d_from}
								isEdit
								sendChange={modelProps.sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							<DateField
								name="skev_d_to"
								label={t('DETAILS_SKILLS_EVALUATIONS_FIELD_DATE_TO')}
								value={modelProps.model.skev_d_to}
								isEdit
								sendChange={modelProps.sendChange}
							/>
						</Grid>
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_BACK')}</Button>
				<Button type="submit" form="skillsEvaluationsEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
